import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { searchContext } from '../../../../context/Context';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';
import dropdownLists from '../../DropdownLists';
import { renderListsFromObjects } from '../renders/CommonAtributes';
import { getHeaderProps, getCategoryByTab } from '../config/helpers';
import { createSelectFilter } from '../filters/filterConfigs';
import { renderChemblLinks } from '../renders/linkRenderers';
import { ChemicalRenderProps, ChemicalColumnsContext } from '../../types/index';
import RestrictedContent from '../../components/RestrictedContent';
import RestrictedHeader from '../../components/RestrictedHeader';
import { renderDefaultValueCols } from '@/renderDefaultValueCols';

export const getChemicalColumns = (context: ChemicalColumnsContext) => {
  const { tab, isGDA, isVDA, isAcademic, isAuthenticated } = context;
  const headerProps = getHeaderProps();

  const isChemicalRestricted = isAuthenticated && isAcademic;

  return [
    {
      name: 'chemPrefName',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Chemical Official Name">Chemical Name</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 240,
      render: row => {
        let chemPrefNames = [];

        // Caso original
        if (row.data.chemPrefName) {
          chemPrefNames = Array.isArray(row.data.chemPrefName)
            ? row.data.chemPrefName
            : [row.data.chemPrefName];
        } else if (Array.isArray(row.data.chemsIncludedInEvidenceBySource)) {
          if (row.data.chemsIncludedInEvidenceBySource.length === 0) {
            chemPrefNames = ['-'];
          } else {
            chemPrefNames = row.data.chemsIncludedInEvidenceBySource
              .map(chem => chem.chemPrefName)
              .filter(Boolean);
          }
        }

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {Array.isArray(chemPrefNames) ? chemPrefNames.join(' | ') : chemPrefNames}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemID',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Chemical ID">Chembl ID</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 205,
      render: row => {
        let chemID = [];

        // Caso original
        if (row.data.chemID) {
          chemID = Array.isArray(row.data.chemID) ? row.data.chemID : [row.data.chemID];

          // Fallback
        } else if (Array.isArray(row.data.chemsIncludedInEvidenceBySource)) {
          if (row.data.chemsIncludedInEvidenceBySource.length === 0) {
            chemID = ['-'];
          } else {
            chemID = row.data.chemsIncludedInEvidenceBySource.map(chem => chem.chemID).filter(Boolean);
          }
        }

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {chemID.map((id, index) => (
              <span key={id}>
                {id === '-' ? '-' : renderChemblLinks(id)}
                {index < chemID.length - 1 && ' | '}
              </span>
            ))}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemIDs',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Chemicals IDs">Chembl IDs</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const chemIDs =
          data?.chemIDs ??
          data?.chemblIDs ??
          [];

        const displayValue =
          Array.isArray(chemIDs) && chemIDs.length > 0
            ? chemIDs.join(' | ')
            : '-';

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {displayValue}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemsInSentence',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Chemicals in sentence">Chemicals in sentence</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 240,
      render: instance => {
        const result = renderDefaultValueCols(instance?.data?.chemsInSentence);

        const displayValue =
          result === '-'
            ? '-'
            : renderListsFromObjects(
                instance,
                'chemsInSentence',
                'chemPrefName'
              );

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {displayValue}
          </RestrictedContent>
        );
      }
    },

    {
      name: 'chemMoleculeType',
      category: 'Chemical',
      sortable: true,
      header: <span title="">Chemical Type</span>,
      headerProps,
      minWidth: 185,
      ...createSelectFilter(dropdownLists.chemMoleculeType),
      render: ({ data }) => renderDefaultValueCols(data?.chemMoleculeType),
    },

    {
      name: 'chemical_effect',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Chemical Effect">Chemical Effect</span>
        </RestrictedHeader>
      ),
      headerProps,
      columnSize: 10,
      ...createSelectFilter(dropdownLists.chemical_effect || []),
      render: ({ value }) => {
        const result = renderDefaultValueCols(value);

        const displayValue =
          result === '-'
            ? '-'
            : result.replace(/\|/g, ' | ');

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {displayValue}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chebi',
      category: 'Chemical',
      sortable: true,
      header: <span>Chebi ID</span>,
      headerProps,
      minWidth: 200,
      getValue: data => (data.chebi || []).join(', '),
      render: ({ data }: ChemicalRenderProps) => {
        const refs = data?.chebi?.map((item: string) => item.split('___')[0]) ?? [];
        const uniqueRefs = Array.from(new Set(refs));
        if (uniqueRefs.length === 0) {
          return '0';
        }

        return (
          <>
            {uniqueRefs.map((id: string, index: number) => (
              <React.Fragment key={id}>
                <a
                  href={`https://www.ebi.ac.uk/chebi/CHEBI:${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {id}
                  <i
                    className="fa fa-external-link"
                    aria-hidden="true"
                  ></i>
                </a>
                {index < uniqueRefs.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </>
        );
      },
    },

    {
      name: 'drugbank',
      category: 'Chemical',
      sortable: true,
      header: <span>Drugbank ID</span>,
      headerProps,
      minWidth: 200,
      getValue: data => (data.drugbank || []).join(', '),
      render: ({ data }: ChemicalRenderProps) => {
        const refs = (data.drugbank || []).map((item: string) => item.split('___')[0]);
        const uniqueRefs = Array.from(new Set(refs));
        if (uniqueRefs.length === 0) {
          return '-';
        }

        return (
          <React.Fragment>
            {uniqueRefs.map((id: string, index: number) => (
              <React.Fragment key={id}>
                <a href={`https://go.drugbank.com/drugs/${id}`} target="_blank" rel="noopener noreferrer">
                  {id}<i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
                {index < uniqueRefs.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      },
    },

    {
      name: 'mesh',
      category: 'Chemical',
      sortable: true,
      header: <span>Mesh ID</span>,
      headerProps,
      minWidth: 200,
      getValue: data => (data.mesh || []).join(', '),
      render: ({ data }: ChemicalRenderProps) => {
        const refs = data.mesh?.map((item: string) => item.split('___')[0]) || [];
        const uniqueRefs = Array.from(new Set(refs));
        if (uniqueRefs.length === 0) {
          return '-';
        }

        return (
          <React.Fragment>
            {uniqueRefs.map((id: string, index: number) => (
              <React.Fragment key={id}>
                <a
                  href={`https://meshb.nlm.nih.gov/record/ui?ui=${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {id} <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
                {index < uniqueRefs.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      },
    },

    {
      name: 'pubchem',
      category: 'Chemical',
      sortable: true,
      header: <span>Pubchem ID</span>,
      headerProps,
      minWidth: 200,
      getValue: data => (data.pubchem || []).join(', '),
      render: ({ data }: ChemicalRenderProps) => {
        const refs = data.pubchem?.map((item: string) => item.split('___')[0]) || [];
        const uniqueRefs = [...new Set(refs)] as string[];
        if (uniqueRefs.length === 0) {
          return '0';
        }

        return (
          <React.Fragment>
            {uniqueRefs.map((id: string, index: number) => (
              <React.Fragment key={id}>
                <a
                  href={`https://pubchem.ncbi.nlm.nih.gov/compound/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {id} <i className="fa fa-external-link" aria-hidden="true"></i>
                </a>
                {index < uniqueRefs.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      },
    },

    {
      name: 'chemNumPublications',
      category: 'Chemical',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Publications mentioning the chemical">N PMIDs</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 205,
      render: ({ data }) => {
        let result = renderDefaultValueCols(data?.chemNumPublications);
        if (result === '-') {
          result = 0;
        }

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {result}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemNumGDAs',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Gene-Disease Associations related to this chemical">N GDAs</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 240,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (tab === 'CHEMICALS' || tab === 'GDA' || tab === 'VDA') {
          newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'GDA',
            searchItems: [{ id: data.chemID }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;
        
        let result = renderDefaultValueCols(data?.chemNumGDAs);
        if (result === '-') {
          result = 0;
        }

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            <Link className="tab-links" to={newUrl}>
              {result}
            </Link>
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemNumVDAs',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Variant-Disease Associations related to this chemical">N VDAs</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 230,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (['CHEMICALS', 'GDA', 'VDA'].includes(tab)) {
          newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'VDA',
            searchItems: [{ id: data.chemID }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;
        
        let result = renderDefaultValueCols(data?.chemNumVDAs);
        if (result === '-') {
          result = 0;
        }

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            <Link className="tab-links" to={newUrl}>
              {result}
            </Link>
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemNumPublicationsGDAs',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: (
        <span title="Number of Publications mentioning all disease-gene association containing the chemical">
          N PMIDs all GDAs
        </span>
      ),
      headerProps,
      minWidth: 230,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (['CHEMICALS', 'GDA', 'VDA'].includes(tab)) {
          newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'GDA',
            searchItems: [{ id: data.chemID }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;

        let result = renderDefaultValueCols(data?.chemNumPublicationsGDAs);
        if (result === '-') {
          result = 0;
        }

        return (
          <Link className="tab-links" to={newUrl}>
            {result}
          </Link>
        );
      },
    },

    {
      name: 'chemNumPublicationsVDAs',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: (
        <span title="Number of Publications mentioning all disease-variant association containing the chemical">
          N PMIDs all VDAs
        </span>
      ),
      headerProps,
      minWidth: 230,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (['CHEMICALS', 'GDA', 'VDA'].includes(tab)) {
          newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'GDA',
            searchItems: [{ id: data.chemID }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;

        let result = renderDefaultValueCols(data?.chemNumPublicationsVDAs);
        if (result === '-') {
          result = 0;
        }

        return (
          <Link className="tab-links" to={newUrl}>
            {result}
          </Link>
        );
      },
    },
  ];
};
